/**
 * NFL RTDB Configuration
 * Centralized configuration for NFL Firebase Realtime Database caching
 */

// Firebase RTDB instance for NFL (separate from football)
export const NFL_RTDB_URL = 'https://just-rhythm-445002-s8-e92d0-2d6f5.firebaseio.com/';

// Cache paths structure
export const CACHE_PATHS = {
  // Root path for all NFL cache
  ROOT: 'cache/nfl',
  
  // Games caching paths
  GAMES: 'cache/nfl/games',
  GAMES_DATES: 'cache/nfl/games/dates',
  GAMES_WEEKS: 'cache/nfl/games/weeks',
  GAMES_LIVE: 'cache/nfl/games/live',
  
  // Other data types
  STANDINGS: 'cache/nfl/standings',
  PLAYERS: 'cache/nfl/players',
  TEAMS: 'cache/nfl/teams',
  ODDS: 'cache/nfl/odds',
  
  // Metadata
  STATS: 'cache/nfl/metadata/stats',
  POPULAR: 'cache/nfl/metadata/popular'
};

// TTL configurations (in milliseconds)
export const TTL_CONFIG = {
  // Live data - very short TTL
  LIVE_GAMES: 30 * 1000,           // 30 seconds
  LIVE_ODDS: 60 * 1000,             // 1 minute
  
  // Game data - moderate TTL
  WEEK_GAMES: 5 * 60 * 1000,       // 5 minutes
  DATE_GAMES: 5 * 60 * 1000,       // 5 minutes
  UPCOMING_GAMES: 10 * 60 * 1000,  // 10 minutes
  
  // Completed games - longer TTL
  PAST_GAMES: 60 * 60 * 1000,      // 1 hour
  HISTORICAL_GAMES: 24 * 60 * 60 * 1000, // 24 hours
  
  // Standings and stats - longer TTL
  STANDINGS: 6 * 60 * 60 * 1000,   // 6 hours
  CONFERENCE_STANDINGS: 6 * 60 * 60 * 1000, // 6 hours
  
  // Static data - very long TTL
  TEAMS: 24 * 60 * 60 * 1000,      // 24 hours
  PLAYERS: 12 * 60 * 60 * 1000,    // 12 hours
  PLAYER_STATS: 6 * 60 * 60 * 1000, // 6 hours
  
  // Odds data
  PREMATCH_ODDS: 5 * 60 * 1000,    // 5 minutes
  
  // Default fallback
  DEFAULT: 5 * 60 * 1000           // 5 minutes
};

// Week mappings for NFL season
export const WEEK_MAPPINGS = {
  // Preseason
  PRESEASON: {
    week1: { start: '2025-08-08', end: '2025-08-12', name: 'Preseason Week 1' },
    week2: { start: '2025-08-15', end: '2025-08-19', name: 'Preseason Week 2' },
    week3: { start: '2025-08-22', end: '2025-08-26', name: 'Preseason Week 3' },
    week4: { start: '2025-08-29', end: '2025-08-31', name: 'Preseason Week 4' }
  },
  
  // Regular season (2025)
  REGULAR: {
    week1: { start: '2025-09-04', end: '2025-09-08', name: 'Week 1' },
    week2: { start: '2025-09-11', end: '2025-09-15', name: 'Week 2' },
    week3: { start: '2025-09-18', end: '2025-09-22', name: 'Week 3' },
    week4: { start: '2025-09-25', end: '2025-09-29', name: 'Week 4' },
    week5: { start: '2025-10-02', end: '2025-10-06', name: 'Week 5' },
    week6: { start: '2025-10-09', end: '2025-10-13', name: 'Week 6' },
    week7: { start: '2025-10-16', end: '2025-10-20', name: 'Week 7' },
    week8: { start: '2025-10-23', end: '2025-10-27', name: 'Week 8' },
    week9: { start: '2025-10-30', end: '2025-11-03', name: 'Week 9' },
    week10: { start: '2025-11-06', end: '2025-11-10', name: 'Week 10' },
    week11: { start: '2025-11-13', end: '2025-11-17', name: 'Week 11' },
    week12: { start: '2025-11-20', end: '2025-11-24', name: 'Week 12' },
    week13: { start: '2025-11-27', end: '2025-12-01', name: 'Week 13' },
    week14: { start: '2025-12-04', end: '2025-12-08', name: 'Week 14' },
    week15: { start: '2025-12-11', end: '2025-12-15', name: 'Week 15' },
    week16: { start: '2025-12-18', end: '2025-12-22', name: 'Week 16' },
    week17: { start: '2025-12-25', end: '2025-12-29', name: 'Week 17' },
    week18: { start: '2026-01-02', end: '2026-01-05', name: 'Week 18' }
  },
  
  // Playoffs (2026)
  PLAYOFFS: {
    wildcard: { start: '2026-01-11', end: '2026-01-13', name: 'Wild Card' },
    divisional: { start: '2026-01-18', end: '2026-01-19', name: 'Divisional' },
    conference: { start: '2026-01-26', end: '2026-01-26', name: 'Conference' },
    superbowl: { start: '2026-02-09', end: '2026-02-09', name: 'Super Bowl' }
  }
};

// Cache key generation helpers
export const getCacheKey = (type, ...params) => {
  // Sanitize parameters to create valid Firebase keys
  const sanitized = params
    .filter(p => p !== null && p !== undefined)
    .map(p => String(p).replace(/[.#$\[\]]/g, '_'))
    .join('_');
  
  return sanitized;
};

// Determine TTL based on game status
export const getTTLForGame = (game) => {
  const status = game?.game?.status?.short || game?.status?.short || '';
  
  // Live game statuses
  const liveStatuses = ['Q1', 'Q2', 'Q3', 'Q4', 'OT', 'HT', 'LIVE'];
  if (liveStatuses.includes(status)) {
    return TTL_CONFIG.LIVE_GAMES;
  }
  
  // Completed game statuses
  const completedStatuses = ['FT', 'AOT', 'CANC', 'PST'];
  if (completedStatuses.includes(status)) {
    // Check if game is from today
    const gameDate = game?.game?.date?.date || game?.date;
    const today = new Date().toISOString().split('T')[0];
    
    if (gameDate === today) {
      return TTL_CONFIG.PAST_GAMES;
    }
    return TTL_CONFIG.HISTORICAL_GAMES;
  }
  
  // Upcoming games
  return TTL_CONFIG.UPCOMING_GAMES;
};

// Debug flag
export const DEBUG_CACHE = process.env.NODE_ENV === 'development';

// Logging helper
export const logCache = (level, message, data = null) => {
  if (DEBUG_CACHE) {
    const prefix = '🏈 [NFL-RTDB]';
    const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
    
    switch (level) {
      case 'hit':
        console.log(`${prefix} ✅ ${timestamp} - ${message}`, data || '');
        break;
      case 'miss':
        console.log(`${prefix} ❌ ${timestamp} - ${message}`, data || '');
        break;
      case 'set':
        console.log(`${prefix} 💾 ${timestamp} - ${message}`, data || '');
        break;
      case 'error':
        console.error(`${prefix} 🚨 ${timestamp} - ${message}`, data || '');
        break;
      case 'info':
      default:
        console.log(`${prefix} ℹ️ ${timestamp} - ${message}`, data || '');
        break;
    }
  }
};

// Helper to determine if we should use cache based on data freshness
export const shouldUseCache = (cacheData) => {
  if (!cacheData) return false;
  
  const { ttl, lastUpdated } = cacheData;
  const now = Date.now();
  const age = now - lastUpdated;
  
  return age < ttl;
};

// Popular teams for pre-warming cache
export const POPULAR_TEAMS = [
  { id: 33, name: 'New England Patriots' },
  { id: 1, name: 'Dallas Cowboys' },
  { id: 17, name: 'Green Bay Packers' },
  { id: 20, name: 'Kansas City Chiefs' },
  { id: 4, name: 'Buffalo Bills' },
  { id: 29, name: 'Philadelphia Eagles' },
  { id: 24, name: 'San Francisco 49ers' },
  { id: 21, name: 'Los Angeles Rams' }
];

// Current week helper
export const getCurrentWeek = () => {
  const now = new Date();
  const currentDate = now.toISOString().split('T')[0];
  
  // Check all week types
  for (const [weekId, range] of Object.entries(WEEK_MAPPINGS.REGULAR)) {
    if (currentDate >= range.start && currentDate <= range.end) {
      return { weekId, type: 'regular', ...range };
    }
  }
  
  for (const [weekId, range] of Object.entries(WEEK_MAPPINGS.PLAYOFFS)) {
    if (currentDate >= range.start && currentDate <= range.end) {
      return { weekId, type: 'playoffs', ...range };
    }
  }
  
  for (const [weekId, range] of Object.entries(WEEK_MAPPINGS.PRESEASON)) {
    if (currentDate >= range.start && currentDate <= range.end) {
      return { weekId, type: 'preseason', ...range };
    }
  }
  
  // Default to week 1 if no match
  return { 
    weekId: 'week1', 
    type: 'regular', 
    ...WEEK_MAPPINGS.REGULAR.week1 
  };
};

// Export all configurations
export default {
  NFL_RTDB_URL,
  CACHE_PATHS,
  TTL_CONFIG,
  WEEK_MAPPINGS,
  getCacheKey,
  getTTLForGame,
  DEBUG_CACHE,
  logCache,
  shouldUseCache,
  POPULAR_TEAMS,
  getCurrentWeek
};